import { useRef, useState } from 'react';
import { Play } from 'lucide-react';

/**
 * VideoPresentacion — un vídeo propio (mp4) en un marco 16:9, con póster y un
 * botón de play a medida.
 *
 * ── POR QUÉ ASÍ ─────────────────────────────────────────────────────────────
 *
 *  - **`preload="none"`.** El vídeo no se descarga hasta que alguien pulsa
 *    play. Un mp4 de 10 MB precargado en una página de marketing es justo lo
 *    que hunde el LCP y el consumo de datos en móvil.
 *  - **El póster es la imagen que se ve.** Sin póster, un `<video>` con
 *    `preload="none"` es un rectángulo negro; por eso el marco lleva su propio
 *    fondo de marca debajo.
 *  - **Play propio, controles nativos después.** El botón grande es la llamada
 *    clara; al pulsarlo se quita y quedan los controles del navegador, que ya
 *    resuelven teclado, pantalla completa y subtítulos.
 *
 * Mientras no haya `src`, pinta el hueco con un «muy pronto» en vez de un
 * reproductor roto: la sección se ve diseñada desde el primer día.
 */
export default function VideoPresentacion({
  src,
  poster,
  subtitulos,
  titulo,
  proximamente,
  reproducir,
}) {
  const ref = useRef(null);
  const [reproduciendo, setReproduciendo] = useState(false);

  const play = () => {
    setReproduciendo(true);
    // `play()` devuelve una promesa que el navegador puede rechazar (p. ej.
    // ahorro de datos). Si pasa, quedan los controles nativos a la vista.
    ref.current?.play()?.catch(() => {});
  };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-[20px] md:rounded-[28px] bg-v3-ink v3-shadow">
      {/* Fondo de marca: el resplandor ámbar que se ve antes que el póster y
          cuando aún no hay vídeo. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(60%_80%_at_70%_20%,rgba(254,174,44,.35),transparent_70%),radial-gradient(50%_60%_at_15%_90%,rgba(254,174,44,.18),transparent_70%)]"
      />

      {src ? (
        <>
          <video
            ref={ref}
            className="relative w-full h-full object-cover"
            src={src}
            poster={poster || undefined}
            preload="none"
            playsInline
            controls={reproduciendo}
            onPlay={() => setReproduciendo(true)}
            aria-label={titulo}
          >
            {subtitulos ? (
              <track kind="captions" src={subtitulos} srcLang="es" label="Español" default />
            ) : null}
          </video>

          {!reproduciendo ? (
            <button
              type="button"
              onClick={play}
              aria-label={reproducir}
              className="group absolute inset-0 flex items-center justify-center focus-visible:outline-none"
            >
              <span className="flex items-center justify-center w-[72px] h-[72px] md:w-[88px] md:h-[88px] rounded-full bg-v3-gold text-v3-ink shadow-[0_10px_30px_rgba(254,174,44,.45)] transition-transform group-hover:scale-105 group-focus-visible:ring-4 group-focus-visible:ring-white/70">
                <Play className="w-7 h-7 md:w-8 md:h-8 ml-1" fill="currentColor" aria-hidden="true" />
              </span>
            </button>
          ) : null}
        </>
      ) : (
        <div className="relative h-full flex flex-col items-center justify-center gap-4 px-6 text-center">
          <span
            aria-hidden="true"
            className="flex items-center justify-center w-[64px] h-[64px] md:w-[80px] md:h-[80px] rounded-full border border-white/25 bg-white/10 text-white/80"
          >
            <Play className="w-6 h-6 md:w-7 md:h-7 ml-1" aria-hidden="true" />
          </span>
          <p className="text-[15px] md:text-[17px] font-semibold text-white/85">{proximamente}</p>
        </div>
      )}
    </div>
  );
}
